/**
 * Equipment modules: a screen per class of airfield equipment, built on the
 * platform's summary of a slice of the estate (`GET /v1/assets/summary`).
 *
 * The platform knows nothing about lights or meters; a module is where this
 * application says which asset types belong together and what to look at on
 * them. Every figure here is computed from what the platform returned, and
 * says what it was computed from:
 *
 *  - "without an open defect" is what it says, not serviceability as a
 *    regulator defines it — that is the platform's KPI, shown beside it when
 *    the pack declares one;
 *  - lamp life is hours run over hours rated, only where both are recorded,
 *    and the number of lights that record neither is reported with it;
 *  - an asset type a module expects that the installed packs do not declare is
 *    named as missing, never shown as a count of zero.
 */
import type { WorkOrder } from "./workOrders";

export interface AttributeBreakdown {
  values: { value: string; count: number }[];
  notRecorded: number;
}

export interface AssetTypeSummary {
  key: string;
  name: string;
  domain?: string;
  category?: string;
  assets: number;
  scored: number;
  conditionMean: number | null;
  located: number;
  byStatus: Record<string, number>;
  byCriticality: Record<string, number>;
  attributes: Record<string, AttributeBreakdown>;
}

export interface OpenDefect {
  id: string;
  assetTag: string;
  assetType: string;
  defectType?: string;
  defectTypeName?: string;
  severity: number;
  status: string;
  summary: string;
  detectedAt: string;
  dueAt?: string;
  zoneCode?: string;
}

export interface EstateSummary {
  assetTypes: AssetTypeSummary[];
  unknownAssetTypes: string[];
  assetsTotal: number;
  defects: {
    open: number;
    raised30d: number;
    resolved30d: number;
    assetsWithOpen: number;
    openList: OpenDefect[];
    openListTotal: number;
    byTypeSeverity: { defectType: string; severity: number; count: number; assets: number }[];
  };
  alerts: {
    open: number;
    list: { id: string; title: string; kind: string; severity: number; status: string; raisedAt: string; assetTag: string; assetType: string }[];
  };
  readings: {
    assetType: string;
    measurement: string;
    measurementName: string;
    unit?: string;
    readings: number;
    assets: number;
    firstAt: string;
    latestAt: string;
  }[];
  latestReadings: {
    assetTag: string;
    assetType: string;
    measurement: string;
    value: number;
    observedAt: string;
    attrs: Record<string, unknown>;
  }[];
}

export const NOTE_CATEGORIES = ["observation", "recommendation", "handover", "follow_up"] as const;
export type NoteCategory = (typeof NOTE_CATEGORIES)[number];
export const NOTE_CATEGORY_LABEL: Record<NoteCategory, string> = {
  observation: "Observation",
  recommendation: "Recommendation",
  handover: "Handover",
  follow_up: "Follow-up",
};

export interface SiteNote {
  id: string;
  topic: string;
  category: NoteCategory;
  body: string;
  assetTag?: string;
  createdAt: string;
  createdBy?: string;
}

export interface NewNoteInput {
  topic: string;
  category: NoteCategory;
  body: string;
  assetTag?: string;
}

/* ---- the modules ------------------------------------------------------------------ */

export interface EquipmentModule {
  key: string;
  route: string;
  title: string;
  /** One line under the title. */
  lede: string;
  /** Asset types that make up the module. */
  assetTypes: string[];
  /** Types the module would show if a pack declared them; named as missing when not. */
  expectedTypes?: { key: string; label: string }[];
  /** Attributes worth breaking down, per asset type, with their labels. */
  attributes: Record<string, Record<string, string>>;
  /** Checklist templates whose rounds belong here. */
  inspectionTemplates: string[];
  /** Platform KPIs that measure this module. */
  kpis: string[];
  /** Hours run against hours rated: the measurement and the attribute that rates it. */
  life?: { measurement: string; ratedAttribute: string; label: string };
  /** Recorded tests shown on the module, latest against the one before. */
  tests?: { testType: string; title: string; unit: string; groupBy: { key: string; label: string }[] }[];
  /** Consumption trends from the module's cumulative meters. */
  consumption?: boolean;
  notesTopic: string;
}

export const EQUIPMENT_MODULES: readonly EquipmentModule[] = [
  {
    key: "markings",
    route: "/markings",
    title: "Marking & visual aids",
    lede: "Markings and signs: reflectivity, defects, work orders, and notes.",
    assetTypes: ["surface_marking", "vertical_sign"],
    attributes: {
      surface_marking: { marking_type: "Marking type", colour: "Colour" },
      vertical_sign: { illuminated: "Illuminated", legend: "Legend" },
    },
    inspectionTemplates: [],
    kpis: [],
    tests: [
      {
        testType: "retroreflectivity",
        title: "Retro-reflectivity",
        unit: "mcd/m²/lx",
        groupBy: [
          { key: "colour", label: "Color" },
          { key: "marking_type", label: "Marking type" },
        ],
      },
    ],
    notesTopic: "markings",
  },
  {
    key: "agl",
    route: "/agl",
    title: "AGL & nav-aids",
    lede: "Airfield lights and circuits: test results, lamp life, night rounds, and nav aids.",
    assetTypes: ["agl_light", "agl_circuit"],
    expectedTypes: [
      { key: "ils_localizer", label: "ILS localizer" },
      { key: "ils_glide_path", label: "ILS glide path" },
      { key: "dvor", label: "DVOR" },
      { key: "dme", label: "DME" },
      { key: "ndb", label: "NDB" },
    ],
    attributes: {
      agl_light: { light_type: "Light type", circuit_tag: "Circuit", mounting: "Mounting", colour: "Colour", lamp_type: "Lamp type" },
      agl_circuit: { circuit_no: "Circuit number" },
    },
    inspectionTemplates: ["agl_night"],
    kpis: ["agl_serviceability_pct"],
    life: { measurement: "burn_hours", ratedAttribute: "rated_hours", label: "Lamp life" },
    tests: [
      {
        testType: "photometric",
        title: "Photometric testing",
        unit: "% of specified intensity",
        groupBy: [
          { key: "circuit_tag", label: "Circuit" },
          { key: "light_type", label: "Light type" },
        ],
      },
    ],
    notesTopic: "agl",
  },
  {
    key: "electrical",
    route: "/electrical",
    title: "Electrical & utilities",
    lede: "Meters, solar, and drainage: usage trends, silent meters, checks, and notes.",
    assetTypes: ["electricity_meter", "water_meter", "solar_array", "drainage_structure"],
    attributes: {
      electricity_meter: { meter_role: "Meter role", serves: "Serves" },
      water_meter: { meter_role: "Meter role", serves: "Serves" },
      drainage_structure: { structure_type: "Structure type" },
    },
    inspectionTemplates: ["meter_verification_quarterly"],
    kpis: ["metered_coverage_pct", "unaccounted_energy_pct", "renewable_fraction_pct", "electricity_consumption_kwh", "water_consumption_m3"],
    consumption: true,
    notesTopic: "electrical",
  },
];

/* ---- computations ------------------------------------------------------------------- */

/** "runway_edge" → "Runway edge"; true → "Yes". */
export function valueLabel(v: string): string {
  if (v === "true") return "Yes";
  if (v === "false") return "No";
  const words = v.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface DefectFree {
  assets: number;
  withOpen: number;
  pct: number | null;
  /** False when the open-defect list the platform returned was capped, so per-type counts are a floor. */
  complete: boolean;
}

/** Per asset type: how many have no open defect. Not a regulatory serviceability figure. */
export function defectFreeByType(s: EstateSummary): Record<string, DefectFree> {
  const complete = s.defects.openList.length >= s.defects.openListTotal;
  const withOpen = new Map<string, Set<string>>();
  for (const d of s.defects.openList) {
    if (!withOpen.has(d.assetType)) withOpen.set(d.assetType, new Set());
    withOpen.get(d.assetType)!.add(d.assetTag);
  }
  return Object.fromEntries(
    s.assetTypes.map((t) => {
      const n = withOpen.get(t.key)?.size ?? 0;
      return [t.key, { assets: t.assets, withOpen: n, pct: t.assets ? (100 * (t.assets - n)) / t.assets : null, complete }];
    }),
  );
}

export interface LifeRow {
  assetTag: string;
  hours: number;
  rated: number;
  pct: number;
  observedAt: string;
}

export interface LifeReport {
  rows: LifeRow[];
  /** Assets of the module with a reading and a rating. */
  judged: number;
  /** With a reading but no rating: hours run, against nothing. */
  unrated: number;
  over80: number;
  over100: number;
}

export function lifeReport(s: EstateSummary, life: NonNullable<EquipmentModule["life"]>): LifeReport {
  const readings = s.latestReadings.filter((r) => r.measurement === life.measurement);
  const rows: LifeRow[] = [];
  let unrated = 0;
  for (const r of readings) {
    const rated = Number(r.attrs?.[life.ratedAttribute]);
    if (!Number.isFinite(rated) || rated <= 0) {
      unrated += 1;
      continue;
    }
    rows.push({ assetTag: r.assetTag, hours: r.value, rated, pct: (100 * r.value) / rated, observedAt: r.observedAt });
  }
  rows.sort((a, b) => b.pct - a.pct || a.assetTag.localeCompare(b.assetTag));
  return {
    rows,
    judged: rows.length,
    unrated,
    over80: rows.filter((r) => r.pct >= 80).length,
    over100: rows.filter((r) => r.pct >= 100).length,
  };
}

/** The work orders raised against this module's equipment — by the asset type the platform traced them to. */
export function moduleWorkOrders(orders: readonly WorkOrder[], module: EquipmentModule): WorkOrder[] {
  return orders.filter((w) => w.assetType && module.assetTypes.includes(w.assetType));
}

export interface InspectionRollup {
  rounds: number;
  completed: number;
  scheduled: number;
  withFailures: number;
  lastCompletedAt?: string;
  meanScore: number | null;
}

export function inspectionRollup(
  rounds: readonly { template: string; status: string; completedAt?: string; failures: number; score?: number }[],
  templates: readonly string[],
): InspectionRollup {
  const mine = rounds.filter((r) => templates.includes(r.template));
  const done = mine.filter((r) => r.status === "completed");
  const scores = done.map((r) => r.score).filter((x): x is number => x != null);
  return {
    rounds: mine.length,
    completed: done.length,
    scheduled: mine.filter((r) => r.status === "scheduled").length,
    withFailures: done.filter((r) => r.failures > 0).length,
    lastCompletedAt: done.map((r) => r.completedAt).filter(Boolean).sort().at(-1),
    meanScore: scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null,
  };
}

function cell(v: string | number | undefined | null): string {
  if (v == null) return "";
  const s = String(v);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** One CSV of the module: its register counts, open defects and work orders, each as its own section. */
export function moduleCsv(module: EquipmentModule, s: EstateSummary, orders: readonly WorkOrder[]): string {
  const lines: string[] = [`${cell(module.title)}`, ""];
  lines.push("Register", "Asset type,Assets,Located,Condition scored,Without an open defect");
  const free = defectFreeByType(s);
  for (const t of s.assetTypes) {
    lines.push([t.name, t.assets, t.located, t.scored, free[t.key]?.pct == null ? "" : free[t.key].pct!.toFixed(1).replace(/\.0$/, "") + "%"].map(cell).join(","));
  }
  lines.push("", "Open defects", "Asset,Asset type,Defect type,Severity,Status,Summary,Detected,Due");
  for (const d of s.defects.openList) {
    lines.push([d.assetTag, d.assetType, d.defectTypeName, d.severity, d.status, d.summary, d.detectedAt, d.dueAt].map(cell).join(","));
  }
  if (s.defects.openListTotal > s.defects.openList.length)
    lines.push(cell(`… ${s.defects.openListTotal - s.defects.openList.length} more open defects not listed`));
  lines.push("", "Work orders", "Number,Title,Kind,Priority,Status,Asset,Opened,Completed,SLA hours");
  for (const w of orders) {
    lines.push([w.number, w.title, w.kind, `P${w.priority}`, w.status, w.assetTag, w.openedAt, w.completedAt, w.slaHours].map(cell).join(","));
  }
  return lines.join("\r\n") + "\r\n";
}
